/**
 * Map standings data to StatsTable format
 */
import type { FUSportsStandings } from "fansunited-data-layer";
import {
    EntityDisplay,
    FormIndicator,
    type FormEntry,
    type StatsTableColumn,
    type StatsTableLegendItem,
    type StatsTableRow,
} from "fansunited-sports-ui";
import Link from "next/link";
import type { Labels } from "@/lib/labels/types";
import { TeamLogo } from "@/components/ui/TeamLogo";
import type { FUSportsStandingsWithNativeTeamIds } from "@/utilities/data/types";

// Zone colors for standings legend
const ZONE_COLORS: Record<string, string> = {
    championsLeague: "bg-blue-600",
    europaLeague: "bg-green-500",
    uefacup: "bg-green-500",
    conferenceLeague: "bg-yellow-500",
    promotion: "bg-green-600",
    promotionPlayoff: "bg-green-400",
    relegation: "bg-red-500",
    relegationPlayoff: "bg-red-400",
};

// Available column keys
type ColumnKey =
    | "rank"
    | "team"
    | "played"
    | "won"
    | "drawn"
    | "lost"
    | "goalsFor"
    | "goalsAgainst"
    | "goalDifference"
    | "points"
    | "form";

// Default columns to show
export const DEFAULT_COLUMNS: ColumnKey[] = [
    "rank",
    "team",
    "played",
    "won",
    "drawn",
    "lost",
    "goalsFor",
    "goalsAgainst",
    "goalDifference",
    "points",
    "form",
];

// Columns hidden on mobile
const MOBILE_HIDDEN: ColumnKey[] = ["won", "drawn", "lost", "goalsFor", "goalsAgainst", "goalDifference"];

interface Team {
    name: string;
    logo?: string;
    id: string;
    /** Native FansUnited team ID (e.g., "fb:t:8102") - used for URL links when available */
    fansUnitedTeamId?: string | null;
}

// Transform form data to FormEntry array for FormIndicator
function toFormEntries(formData: unknown, team?: Team): FormEntry[] {
    const data = formData as
        | {
              competitorId: string;
              results?: Array<{
                  matchId: string;
                  result: string;
                  score: string;
                  opponent: { id: string; name: string; logo: string };
                  date: string;
              }>;
          }
        | undefined;
    if (!data?.results?.length) return [];

    return data.results.map((r) => {
        const [teamScore, oppScore] = r.score.split("-");
        return {
            matchId: r.matchId,
            outcome: r.result as "W" | "D" | "L",
            date: new Date(r.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
            competitorOne: { id: data.competitorId, name: team?.name || "", image: team?.logo, score: teamScore },
            competitorTwo: { id: r.opponent.id, name: r.opponent.name, image: r.opponent.logo, score: oppScore },
        };
    });
}

// Column definitions with render functions
function getColumn(
    key: ColumnKey,
    labels: Labels["standings"],
    mobileHiddenColumns: ColumnKey[] = MOBILE_HIDDEN,
    teamUrlBuilder?: (teamId: string) => string
): StatsTableColumn {
    const mobileHidden = mobileHiddenColumns.includes(key);

    const columns: Record<ColumnKey, StatsTableColumn> = {
        rank: { key: "rank", header: "#", align: "center", width: "xs" },
        team: {
            key: "team",
            header: labels.table.team,
            align: "left",
            width: "flex",
            render: (v) => {
                const t = v as Team;
                const display = <EntityDisplay name={t.name} image={<TeamLogo src={t.logo} size="sm" />} />;
                if (!teamUrlBuilder) return display;
                const teamIdForUrl = t.fansUnitedTeamId ?? t.id;
                return (
                    <Link prefetch={false} href={teamUrlBuilder(teamIdForUrl)}>
                        {display}
                    </Link>
                );
            },
        },
        played: {
            key: "played",
            header: labels.table.played,
            align: "center",
            width: mobileHidden ? { desktop: "sm", mobile: "none" } : "sm",
        },
        won: {
            key: "won",
            header: labels.table.won,
            align: "center",
            width: mobileHidden ? { desktop: "sm", mobile: "none" } : "sm",
        },
        drawn: {
            key: "drawn",
            header: labels.table.drawn,
            align: "center",
            width: mobileHidden ? { desktop: "sm", mobile: "none" } : "sm",
        },
        lost: {
            key: "lost",
            header: labels.table.lost,
            align: "center",
            width: mobileHidden ? { desktop: "sm", mobile: "none" } : "sm",
        },
        goalsFor: {
            key: "goalsFor",
            header: labels.table.goalsFor,
            align: "center",
            width: mobileHidden ? { desktop: "sm", mobile: "none" } : "sm",
        },
        goalsAgainst: {
            key: "goalsAgainst",
            header: labels.table.goalsAgainst,
            align: "center",
            width: mobileHidden ? { desktop: "sm", mobile: "none" } : "sm",
        },
        goalDifference: {
            key: "goalDifference",
            header: labels.table.goalDifference,
            align: "center",
            width: mobileHidden ? { desktop: "sm", mobile: "none" } : "sm",
        },
        points: {
            key: "points",
            header: labels.table.points,
            align: "center",
            width: "md",
            className: "font-bold",
        },
        form: {
            key: "form",
            header: labels.table.form,
            align: "right",
            width: { desktop: "xl", mobile: "none" },
            render: (v, row) => {
                if (!v) return null;
                const entries = toFormEntries(v, row.team as Team);
                if (!entries.length) return null;
                return (
                    <FormIndicator
                        form={entries.reverse()}
                        matchSeparator="last"
                        variant="embedded"
                        mode="responsive"
                        styles={{ item: { container: "rounded-md" } }}
                    />
                );
            },
        },
    };

    return columns[key];
}

/**
 * Map FUSportsStandings to StatsTable format.
 * Accepts both regular FUSportsStandings and FUSportsStandingsWithNativeTeamIds.
 * When native team IDs are available, they will be used for team page links.
 */
export function mapStandingsToTable(
    standings: FUSportsStandings | FUSportsStandingsWithNativeTeamIds,
    labels: Labels["standings"],
    columnKeys: ColumnKey[] = DEFAULT_COLUMNS,
    mobileHiddenColumns: ColumnKey[] = MOBILE_HIDDEN,
    teamUrlBuilder?: (teamId: string) => string
): { columns: StatsTableColumn[]; rows: StatsTableRow[]; legend?: StatsTableLegendItem[] } {
    const columns = columnKeys.map((key) => getColumn(key, labels, mobileHiddenColumns, teamUrlBuilder));

    const rows: StatsTableRow[] = standings.entries.map((entry) => {
        // Zone from first rule
        let zone: { color: string; label: string } | undefined;
        if (entry.rules?.[0]) {
            const legendItem = standings.metadata.legend.find((l) => l.key === entry.rules![0]);
            if (legendItem) {
                zone = { color: ZONE_COLORS[entry.rules[0]] ?? "bg-gray-500", label: legendItem.name };
            }
        }

        // Extract native team ID if available (from FUSportsStandingsWithNativeTeamIds)
        const competitor = entry.competitor as {
            id: string;
            name: string;
            logo?: string;
            fansUnitedTeamId?: string | null;
        };

        // Build row data - include fansUnitedTeamId for the team column renderer
        const data: Record<string, unknown> = {
            rank: entry.rank,
            team: {
                name: competitor.name,
                logo: competitor.logo,
                id: competitor.id,
                fansUnitedTeamId: competitor.fansUnitedTeamId,
            },
            played: entry.stats.played ?? "-",
            won: entry.stats.won ?? "-",
            drawn: entry.stats.drawn ?? "-",
            lost: entry.stats.lost ?? "-",
            goalsFor: entry.stats.goalsFor ?? "-",
            goalsAgainst: entry.stats.goalsAgainst ?? "-",
            goalDifference: entry.stats.goalDifference ?? "-",
            points: entry.stats.points ?? "-",
            form: entry.form,
        };

        const row: StatsTableRow = { id: competitor.id, zone };
        for (const key of columnKeys) row[key] = data[key];
        return row;
    });

    const legend = standings.metadata.legend.map((l) => ({
        color: ZONE_COLORS[l.key] ?? "bg-gray-500",
        label: l.name,
    }));

    return { columns, rows, legend: legend.length ? legend : undefined };
}
